import { type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	unstable_useRoute,
	useParams,
	useRouteError,
} from 'react-router'
import { DOMAIN_CONFIGS } from '#app/config/domains.ts'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const rootData = unstable_useRoute('root')?.loaderData
	const domainConfig = rootData?.domainConfig || DOMAIN_CONFIGS.localhost

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	return (
		<div className="container mx-auto flex flex-col items-center justify-center p-20">
			<div className="text-4xl font-bold">
				{isRouteErrorResponse(error)
					? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
							error,
							params,
						})
					: unexpectedErrorHandler(error)}
			</div>

			<p className="mt-6 text-lg">
				Contact us at{' '}
				<a className="underline" href={`mailto:${domainConfig.supportEmail}`}>
					{domainConfig.supportEmail}
				</a>{' '}
				if the issue persists.
			</p>
		</div>
	)
}

function getErrorMessage(error: unknown) {
	if (typeof error === 'string') return error
	if (
		error &&
		typeof error === 'object' &&
		'message' in error &&
		typeof error.message === 'string'
	) {
		return error.message
	}
	console.error('Unable to get error message for error', error)
	return 'Unknown Error'
}
